import { useEffect, useLayoutEffect } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, useLocation } from 'react-router'

import { SidebarSeparator } from '@/components/sidebar'
import { isProduction } from '@/config/config'
import { useCurrentUser } from '@/models/user'
import { BrandAssetLinks } from './BrandAssetLinks'
import { DesignSystemNav } from './DesignSystemNav'
import { IntroResources } from './IntroResources'
import { LogoHomeLink } from './LogoHomeLink'
import { ActionsSheetSection } from './sections/ActionsSheetSection'
import { AgentSection } from './sections/AgentSection'
import { AvatarsSection } from './sections/AvatarsSection'
import { BadgeSection } from './sections/BadgeSection'
import { BannersSection } from './sections/BannersSection'
import { BrokerActionsSection } from './sections/BrokerActionsSection'
import { ButtonsSection } from './sections/ButtonsSection'
import { CardCheckboxSection } from './sections/CardCheckboxSection'
import { CardRadioSection } from './sections/CardRadioSection'
import { CardsSection } from './sections/CardsSection'
import { CarrierCardSection } from './sections/CarrierCardSection'
import { ChatSection } from './sections/ChatSection'
import { ColorSection } from './sections/ColorSection'
import { ComboboxSection } from './sections/ComboboxSection'
import { FieldsSection } from './sections/FieldsSection'
import { FileChipSection } from './sections/FileChipSection'
import { FooterSection } from './sections/FooterSection'
import { FormSection } from './sections/FormSection'
import { HowToMakeStuffSection } from './sections/HowToMakeStuffSection'
import { IconsSection } from './sections/IconsSection'
import { IdeaCardSection } from './sections/IdeaCardSection'
import { LinksSection } from './sections/LinksSection'
import { LoadingSection } from './sections/LoadingSection'
import { LogoSection } from './sections/LogoSection'
import { MultilineFieldSection } from './sections/MultilineFieldSection'
import { NavigationSection } from './sections/NavigationSection'
import { PaginationSection } from './sections/PaginationSection'
import { PopoverSection } from './sections/PopoverSection'
import { QuoteCardSection } from './sections/QuoteCardSection'
import { SearchableSelectSection } from './sections/SearchableSelectSection'
import { SegmentedControlSection } from './sections/SegmentedControlSection'
import { SkeletonsSection } from './sections/SkeletonsSection'
import { SkillsSection } from './sections/SkillsSection'
import { SliderSection } from './sections/SliderSection'
import { SpacingSection } from './sections/SpacingSection'
import { StepperSection } from './sections/StepperSection'
import { TableSection } from './sections/TableSection'
import { TableToolbarSection } from './sections/TableToolbarSection'
import { TagInputSection } from './sections/TagInputSection'
import { TimelineSection } from './sections/TimelineSection'
import { ToastSection } from './sections/ToastSection'
import { ToolCallSection } from './sections/ToolCallSection'
import { TooltipSection } from './sections/TooltipSection'
import { TypeSection } from './sections/TypeSection'
import { ValuePropsSection } from './sections/ValuePropsSection'

const SCROLL_POSITIONS_KEY = 'react-router-scroll-positions'

/**
 * CAI-staff-only design-system reference: tokens (color, type, spacing) and
 * every shared/generic component, each rendered live from the real source.
 * Deliberately mounted outside the app shell — no sidebar, no app chrome —
 * so the marketing design language is shown on its own canvas.
 */
export function DesignSystem() {
  // Staff-only in production; dev/staging serve the page without auth.
  const { user } = useCurrentUser({ enabled: isProduction })
  const location = useLocation()
  const canView = !isProduction || Boolean(user?.isCaiAdmin)

  // Drop this route's saved offset before <ScrollRestoration> reads it, so its hash-or-top fallback runs.
  useLayoutEffect(() => {
    try {
      const stored = sessionStorage.getItem(SCROLL_POSITIONS_KEY)
      if (!stored) return
      const positions = JSON.parse(stored) as Record<string, number>
      if (!(location.key in positions)) return
      delete positions[location.key]
      sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(positions))
    } catch {
      // Blocked or unparseable storage means there is no offset to undo.
    }
  }, [location.key])

  // Waits for the content: in production the first render is the auth guard, with no #section target yet.
  useEffect(() => {
    if (!canView) return
    const id = window.location.hash.slice(1)
    const target = id ? document.getElementById(id) : null
    if (target) {
      target.scrollIntoView()
    } else {
      window.scrollTo(0, 0)
    }
  }, [canView])

  if (isProduction && !user?.isCaiAdmin) {
    return <Navigate to="/insure" replace />
  }

  return (
    <div className="bg-ink-50 flex min-h-screen shrink-0">
      <Helmet>
        <title>Design System</title>
      </Helmet>
      {/* The customer account sidebar's expanded treatment; the rows scroll so long groups stay reachable. */}
      <aside className="bg-surface border-ink-200 sticky top-0 hidden h-svh w-64 shrink-0 flex-col border-r text-neutral-600 lg:flex">
        <div className="flex min-h-8 items-center p-2">
          <div className="ml-2">
            <LogoHomeLink />
          </div>
        </div>
        <div className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain">
          <SidebarSeparator />
          <DesignSystemNav />
        </div>
      </aside>
      <div className="mx-auto flex max-w-(--maxw) min-w-0 flex-1 px-6 py-14">
        <main className="flex min-w-0 flex-1 flex-col gap-10">
          <header id="intro" className="scroll-mt-10">
            <h1 className="h1-hero text-ink">Design System</h1>
            <p className="text-md text-ink-500 mt-6 max-w-2xl leading-relaxed">
              Cai&apos;s Design System lives in code in the Insure repo, not a static brand deck that falls
              out of date, or a Figma library that never quite matches what ships. Use it whenever you&apos;re
              making something a customer will see.
            </p>
            <IntroResources />
            <BrandAssetLinks />
          </header>
          <HowToMakeStuffSection />
          <LogoSection />
          <ColorSection />
          <TypeSection />
          <SpacingSection />
          <IconsSection />
          <SkillsSection />
          <ButtonsSection />
          <LinksSection />
          <BannersSection />
          <ToastSection />
          <NavigationSection />
          <BrokerActionsSection />
          <FooterSection />
          <ValuePropsSection />
          <LoadingSection />
          <TooltipSection />
          <PopoverSection />
          <StepperSection />
          <ChatSection />
          <SkeletonsSection />
          <AvatarsSection />
          <ToolCallSection />
          <AgentSection />
          <BadgeSection />
          <FileChipSection />
          <IdeaCardSection />
          <TimelineSection />
          <ActionsSheetSection />
          <FieldsSection />
          <MultilineFieldSection />
          <SearchableSelectSection />
          <ComboboxSection />
          <TagInputSection />
          <SliderSection />
          <FormSection />
          <CardCheckboxSection />
          <CardRadioSection />
          <SegmentedControlSection />
          <CardsSection />
          <CarrierCardSection />
          <QuoteCardSection />
          <TableSection />
          <PaginationSection />
          <TableToolbarSection />
        </main>
      </div>
    </div>
  )
}
